<template>
  <div>
    <div v-if="organizationLoader">
      <div class="text-center py-4">
        <b-spinner
          type="grow"
          :label="$t('DEBTIA_COMMON_LOADING')"
        />
      </div>
    </div>
    <div v-else-if="!organizationLoader && organizationOptions.length">
      <div class="card rounded mb-1 p-2">
        <b-row class="d-flex justify-content-end">
          <b-col
            class="d-flex align-items-center justify-content-start mb-1"
          >
            <h3>{{ $t('DEBTIA_SELECT_ORGANIZATION') }}</h3>
          </b-col>
          <b-col>
            <v-select
              v-model="organizations"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="organizationOptions"
              :reduce="val => val.value"
              :clearable="false"
              @input="setOrganization"
            />
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="primary"
              size="sm"
              class="font-weight-bold d-flex align-items-center"
              @click="confirmationModal"
            >
              <feather-icon
                icon="PlusIcon"
                size="18"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('DEBTIA_ADD_MORE') }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-card
        class="mb-0"
      >
          <div class="mb-1">
            <b-row>
              <b-col
                cols="12"
                class="d-flex align-items-center justify-content-between"
              >
                <div class="d-flex align-items-center">
                  <label>{{ $t('DEBTIA_SHOWING') }}</label>

                  <v-select
                    v-model="invoicePerPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="false"
                    class="search_by_select per-page-selector d-inline-block mx-50"
                    :options="perPageOptions"
                  />
                  <label>{{ $t('DEBTIA_ENTRIES') }}</label>
                </div>

                <b-form-checkbox
                  v-model="autoImport"
                  switch
                  @change="ToggleAutoImport($event)"
                >
                  {{ $t('DEBTIA_AUTO_IMPORT_EVERYTIME') }}
                </b-form-checkbox>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col>
              <h3 class="mb-1">
                {{ $t('DEBTIA_INVOICES') }}
              </h3>
            </b-col>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1"
            >
              <b-button
                variant="primary"
                size="sm"
                @click="importAllInvoices"
              >
                {{ $t('DEBTIA_IMPORT_ALL') }}
              </b-button>
            </b-col>
          </b-row>
          <b-table
            class="position-relative resource-listing"
            responsive
            :fields="invoicesFields"
            :items="invoiceData"
            hover
            show-empty
            :empty-text="$t('DEBTIA_COMMON_NO_MATCHING_RECORDS')"
          >
            <template #cell(invoice_number)="data">
              {{ data.item.bookedInvoiceNumber ? data.item.bookedInvoiceNumber : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(contact_name)="data">
              {{ data.item.recipient.name ? data.item.recipient.name : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(payment_status)="data">
              {{ data.item.invoiceStatus ? data.item.invoiceStatus == 'Overdue' ? $t('DEBTIA_OVER_DUE') : $t('DEBTIA_DUE') : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(payment_date)="data">
              {{ formattedDate(data.item.dueDate) ? formattedDate(data.item.dueDate) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(gross_amount)="data">
              {{ (data.item.grossAmount) ? convertToDKK(data.item.grossAmount, data.item.currency) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(remainder)="data">
              {{ (data.item.remainder) ? convertToDKK(data.item.remainder, data.item.currency) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(action)="data">
              <b-badge
                v-if="new Date() < new Date(data.item.dueDate)"
                :class="{ 'disabled': data.item.enableImport === 0 }"
                pill
                variant="light-warning"
              >
                {{ $t('DEBTIA_DUE') }}
              </b-badge>
              <b-badge
                v-else-if="parseInt(data.item.netAmount) <= 0"
                :class="'disabled'"
                pill
                variant="light-warning"
              >
                {{ $t('DEBTIA_NOT_DUE') }}
              </b-badge>
              <b-badge
                v-else-if="data.item.isImported === false && data.item.invoiceStatus === 'Overdue'"
                pill
                variant="light-warning"
                style="cursor: pointer;"
                @click="importSingleInvoice(data.item.bookedInvoiceNumber)"
              >
                {{ $t('DEBTIA_IMPORT') }}
              </b-badge>
              <b-badge
                v-else
                pill
                variant="light-success"
              >
                {{ $t('DEBTIA_IMPORTED') }}
              </b-badge>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">{{ $t('DEBTIA_SHOWING') }} {{ invoiceFrom }} to {{ invoiceTo }} of {{ invoiceTotalItems }} {{ $t('DEBTIA_ENTRIES') }}</span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >

                <b-pagination
                  v-model="invoiceCurrentPage"
                  :total-rows="invoiceTotalItems"
                  :per-page="invoicePerPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
      </b-card>

      <b-card
        class="mb-0 mt-3"
      >
          <div class="mb-1">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              >
                <label>{{ $t('DEBTIA_SHOWING') }}</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  class="search_by_select per-page-selector d-inline-block mx-50"
                  :options="perPageOptions"
                />
                <label>{{ $t('DEBTIA_ENTRIES') }}</label>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col>
              <h3 class="mb-1">
                {{ $t('DEBTIA_DEBTORS') }}
              </h3>
            </b-col>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1"
            >
              <b-button
                variant="primary"
                size="sm"
                @click="importAllDebtors"
              >
                {{ $t('DEBTIA_IMPORT_ALL') }}
              </b-button>
            </b-col>
          </b-row>
          <b-table
            class="position-relative resource-listing"
            responsive
            :fields="debtorsFields"
            :items="debtorsData"
            hover
            show-empty
            :empty-text="$t('DEBTIA_COMMON_NO_MATCHING_RECORDS')"
          >
            <template #cell(customerNumber)="data">
              {{ (data.item.customerNumber ) ? data.item.customerNumber : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(name)="data">
              {{ (data.item.name ) ? data.item.name : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(email)="data">
              {{ (data.item.email ) ? data.item.email : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(balance)="data">
              {{ (data.item.balance) ? convertToDKK(data.item.balance, data.item.currency) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(action)="data">
              <b-badge
                v-if="data.item.isImported === false"
                pill
                variant="light-warning"
                style="cursor: pointer;"
                @click="importSingleDebtor(data.item.customerNumber)"
              >
                {{ $t('DEBTIA_IMPORT') }}
              </b-badge>
              <b-badge
                v-else
                pill
                variant="light-success"
              >
                {{ $t('DEBTIA_IMPORTED') }}
              </b-badge>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">{{ $t('DEBTIA_SHOWING') }} {{ from }} to {{ to }} of {{ totalItems }} {{ $t('DEBTIA_ENTRIES') }}</span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >

                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalItems"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
      </b-card>
    </div>
    <div v-else>
      <div class="misc-wrapper">
        <div class="misc-inner p-2 p-sm-3">
          <div class="w-100 text-center">
            <div class="mb-2">
              <p>{{ $t('DEBTIA_ECONOMIC_NOTE') }}</p>
              <b-button
                variant="primary"
                @click="redirectLink"
              >
                {{ $t('DEBTIA_INTEGRATE') }}
              </b-button>
            </div>
            <b-img
              fluid
              :src="imgUrl"
              :alt="$t('DEBTIA_COMMON_COMING_SOON')"
            />
          </div>
        </div>
      </div>
    </div>
    <integrations-modal
      ref="integrationsModal"
      :redirect-link="redirectLink"
    />
  </div>
</template>

<script>
/* eslint-disable global-require */
import {
  BRow, BCol, BButton, BTable, BCard, BPagination, BBadge, BImg, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@axios'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store/index'
import constants from '@/app-configs/constants'
import integrationsModal from './ConfirmationModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BTable,
    BCard,
    BPagination,
    vSelect,
    BBadge,
    BImg,
    BSpinner,
    integrationsModal,
    BFormCheckbox,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      from: 0,
      to: 0,
      totalItems: 0,
      loader: false,
      organizations: '',
      organizationOptions: [],
      debtorsData: [],
      invoiceData: [],
      invoiceCurrentPage: 1,
      invoicePerPage: 10,
      invoiceFrom: 0,
      invoiceTo: 0,
      invoiceTotalItems: 0,
      clientId: JSON.parse(localStorage.getItem('userData')).data.id,
      debtorLoader: false,
      perPageOptions: [10, 25, 50, 100],
      downImg: require('@/assets/images/pages/coming-soon.svg'),
      organizationLoader: true,
      autoImport: true,
    }
  },
  computed: {
    debtorsFields() {
      return [
        { key: 'customerNumber', sortable: true, label: i18n.t('DEBTIA_CUSTOMER_NUMBER') },
        { key: 'name', sortable: true, label: i18n.t('DEBTIA_NAME') },
        { key: 'email', sortable: true, label: i18n.t('DEBTIA_EMAIL') },
        { key: 'balance', label: i18n.t('DEBTIA_BALANCE') },
        { key: 'action', label: i18n.t('DEBTIA_ACTION') },
      ]
    },
    invoicesFields() {
      return [
        { key: 'invoice_number', sortable: true, label: i18n.t('DEBTIA_INVOICE_NUMBER') },
        { key: 'contact_name', sortable: true, label: i18n.t('DEBTIA_CONTACT_NAME') },
        { key: 'payment_status', sortable: true, label: i18n.t('DEBTIA_PAYMENT_STATUS') },
        { key: 'payment_date', sortable: true, label: i18n.t('DEBTIA_DUE_DATE') },
        { key: 'gross_amount', sortable: true, label: i18n.t('DEBTIA_AMOUNT') },
        { key: 'remainder', sortable: true, label: i18n.t('DEBTIA_REMAINING_AMOUNT') },
        { key: 'action', label: i18n.t('DEBTIA_ACTION') },
      ]
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.downImg = require('@/assets/images/pages/error-dark.svg')
        return this.downImg
      }
      return this.downImg
    },
  },
  watch: {
    loader(val) {
      this.$store.commit('app/SET_GLOBAL_LOADING', { show: val || this.debtorLoader, text: this.$t('DEBTIA_INTEGRATION_LOADING') })
    },
    debtorLoader(val) {
      this.$store.commit('app/SET_GLOBAL_LOADING', { show: val || this.loader, text: this.$t('DEBTIA_INTEGRATION_LOADING') })
    },
    currentPage: {
      handler() {
        this.getDebtors()
      },
    },
    perPage: {
      handler() {
        this.currentPage = 1
        this.getDebtors()
      },
    },
    invoicePerPage: {
      handler() {
        this.invoiceCurrentPage = 1
        this.getInvoices()
      },
    },
    invoiceCurrentPage: {
      handler() {
        this.getInvoices()
      },
    },
  },
  created() {
    this.getOrganizations()
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  methods: {
    getOrganizations() {
      this.organizationLoader = true
      axios.get('integrations/economic/get-organizations').then(response => {
        const data = Array.isArray(response.data.data) ? response.data.data : []

        this.organizationOptions = data
        if (this.organizationOptions.length > 0) {
          this.getDebtors()
          this.getInvoices()
        }
        const selected = data.find(org => org.is_selected === 1)
        if (selected) {
          this.organizations = selected.value
          this.autoImport = !!selected.auto_import
        }
        this.organizationLoader = false
      }).catch(error => {
        this.organizationLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_ORGANIZATIONS'),
          },
        })
      })
    },
    getDebtors() {
      this.debtorLoader = true
      axios
        .get(`integrations/economic/get-debtors?per_page=${this.perPage}&page=${this.currentPage - 1}`)
        .then(response => {
          const { collection, pagination } = response.data.data

          this.debtorsData = collection
          this.totalItems = pagination.results

          this.from = pagination.results === 0 ? 0 : pagination.skipPages * this.perPage + 1
          this.to = Math.min((pagination.skipPages + 1) * this.perPage, this.totalItems)
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_ERROR'),
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_DEBTORS'),
            },
          })
        })
        .finally(() => {
          this.debtorLoader = false
        })
    },
    getInvoices() {
      this.loader = true
      axios.get(`integrations/economic/get-invoices?per_page=${this.invoicePerPage}&page=${this.invoiceCurrentPage - 1}`)
        .then(response => {
          const { collection, pagination } = response.data.data

          this.invoiceData = collection
          this.invoiceTotalItems = pagination.resultsWithoutFilter

          this.invoiceFrom = pagination.resultsWithoutFilter === 0 ? 0 : pagination.skipPages * this.invoicePerPage + 1
          this.invoiceTo = Math.min((pagination.skipPages + 1) * this.invoicePerPage, this.invoiceTotalItems)

          this.loader = false
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_ERROR'),
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_INVOICES'),
            },
          })
        })
        .finally(() => {
          this.loader = false
        })
    },
    formattedDate(date) {
      if (!date) return this.$t('DEBTIA_COMMON_INVALID_DATE')
      // Convert the date to a string if it's not already one
      const dateString = String(date)
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-')
        return `${day}-${month}-${year}`
      }
      // Handle invalid date format or other cases
      return this.$t('DEBTIA_COMMON_INVALID_DATE')
    },
    redirectLink() {
      const url = `${constants.economicRedirectUrl}client_id=${this.clientId}`
      window.location.href = url
    },
    setOrganization() {
      axios.post('user-settings/set-default-organization', { organization_id: this.organizations, source: 'economic' }).then(response => {
        if (response.data.status === 200) {
          this.getDebtors()
          this.getInvoices()
        }
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_SET_ORGANIZATION'),
          },
        })
      })
    },
    importSingleDebtor(id) {
      this.debtorLoader = true
      const url = `integrations/economic/import-debtors?customer_id=${id}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedDebtors = response.data.data.failed_to_import_debtors || []
          if (failedDebtors.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedDebtors.map(debtor => `Failed Debtor: ${debtor.customerNumber}: ${debtor.error}`).join('')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: response.data.message || this.$t('DEBTIA_TOAST_DEBTOR_IMPORTED'),
              },
            })
          }
          this.debtorLoader = false
          this.currentPage = 1
          this.getDebtors()
        }
      }).catch(error => {
        this.debtorLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_DEBTOR'),
          },
        })
      })
    },
    importAllDebtors() {
      this.debtorLoader = true
      const url = `integrations/economic/import-debtors?per_page=${this.perPage}&page=${this.currentPage - 1}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedDebtors = response.data.data.failed_to_import_debtors || []
          if (failedDebtors.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedDebtors.map(debtor => `Failed Debtor: ${debtor.customerNumber}: ${debtor.error}`).join('')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: response.data.message || this.$t('DEBTIA_TOAST_DEBTORS_IMPORTED'),
              },
            })
          }
          this.debtorLoader = false
          this.getDebtors()
        }
      }).catch(error => {
        this.debtorLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_DEBTOR'),
          },
        })
      })
    },
    importSingleInvoice(invoiceId) {
      this.loader = true
      const url = `integrations/economic/import-invoice/${invoiceId}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_SUCCESS'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('DEBTIA_TOAST_INVOICE_IMPORTED'),
            },
          })
          this.loader = false
          this.invoiceCurrentPage = 1
          this.getInvoices()
          this.getDebtors()
        }
      }).catch(error => {
        this.loader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_INVOICE'),
          },
        })
      })
    },
    importAllInvoices() {
      this.loader = true
      const url = `integrations/economic/import-invoices?per_page=${this.invoicePerPage}&page=${this.invoiceCurrentPage - 1}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedInvoices = response.data.data.failedToImportInvoices || []
          if (failedInvoices.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedInvoices.map(invoice => `Invoice ${invoice.invoiceNumber}: ${invoice.error}`).join('')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('DEBTIA_TOAST_ALL_INVOICES_IMPORTED'),
              },
            })
          }
          this.loader = false
          this.getInvoices()
          this.getDebtors()
        }
      }).catch(error => {
        this.loader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_INVOICE'),
          },
        })
      })
    },
    confirmationModal() {
      this.$refs.integrationsModal.open()
    },
    convertToDKK(amount, defaultCurrency) {
      return `${amount} ${defaultCurrency}`
      // return new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(amount).toFixed(2))
    },
    ToggleAutoImport(val) {
      const url = `integrations/organizations/${this.organizations}/auto-import`
      axios.post(url, { auto_import: val, source: 'economic' }).then(response => {
        if (response.data.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_SUCCESS'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('DEBTIA_TOAST_AUTO_IMPORT_UPDATED'),
            },
          })
        }
      }).catch(error => {
        const msg = Array.isArray(error.response.data.message) ? error.response.data.message.join(', ') : ''
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: msg || this.$t('DEBTIA_TOAST_FAILED_UPDATE_AUTO_IMPORT'),
          },
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.search_by_select .vs__dropdown-toggle{
  width: 160px;
}

.resource-listing .table-hover {
  tbody {
    tr {
      cursor: default;
    }
  }
}

</style>
